import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Spade, Diamond, Club } from 'lucide-react';

interface GameAlias {
  name: string;
  lang: 'zh' | 'en';
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
}

const gameAliases: GameAlias[] = [
  { name: 'Shengji', lang: 'en', suit: 'hearts' },
  { name: '升级', lang: 'zh', suit: 'spades' },
  { name: 'Tractor', lang: 'en', suit: 'diamonds' },
  { name: '拖拉机', lang: 'zh', suit: 'clubs' },
  { name: '80 Points', lang: 'en', suit: 'hearts' },
  { name: '找朋友', lang: 'zh', suit: 'spades' },
  { name: 'Finding Friends', lang: 'en', suit: 'diamonds' },
];

const CARD_COUNT = 3;

const SUIT_ICONS = {
  hearts: Heart,
  diamonds: Diamond,
  clubs: Club,
  spades: Spade,
};

const SUIT_COLORS = {
  hearts: 'text-red-500',
  diamonds: 'text-red-500',
  clubs: 'text-gray-800',
  spades: 'text-gray-800',
};

export function GameAliases() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    if (!mediaQuery.matches) {
      const interval = setInterval(() => {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % gameAliases.length);
          setTimeout(() => {
            setIsAnimating(false);
          }, 800);
        }, 100);
      }, 5000);

      return () => {
        clearInterval(interval);
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const getVisibleAliases = () => {
    const visible: GameAlias[] = [];
    for (let i = 0; i < CARD_COUNT; i++) {
      visible.push(gameAliases[(currentIndex + i) % gameAliases.length]);
    }
    return visible;
  };

  return (
    <div
      className="flex justify-center items-center relative"
      role="region"
      aria-label="Also known as"
    >
      <div className="relative" style={{ width: '340px', height: '460px' }}>
        <AnimatePresence mode="popLayout">
          {getVisibleAliases().map((alias, index) => {
            const isTopCard = index === 0;
            const stackOffset = index * 12;
            const rotateOffset = index * 2;
            const zIndex = CARD_COUNT - index;
            const SuitIcon = SUIT_ICONS[alias.suit];
            const suitColor = SUIT_COLORS[alias.suit];

            return (
              <motion.div
                key={`${alias.name}-${currentIndex + index}`}
                className="absolute top-0 left-0"
                initial={{
                  x: -50,
                  y: 50,
                  opacity: 0,
                  scale: 0.9,
                  rotate: -5,
                }}
                animate={{
                  x: stackOffset,
                  y: stackOffset,
                  rotate: rotateOffset,
                  scale: isTopCard && isAnimating ? 1.05 : 1,
                  opacity: 1,
                }}
                exit={{
                  x: 350,
                  y: -120,
                  rotate: 25,
                  opacity: 0,
                  scale: 0.8,
                }}
                transition={{
                  duration: prefersReducedMotion ? 0.3 : 0.7,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                style={{ zIndex }}
              >
                <div
                  className={`
                    bg-white rounded-2xl shadow-2xl border border-gray-200
                    flex flex-col items-center justify-center relative
                  `}
                  style={{ width: '280px', height: '380px' }}
                >
                  {/* Top left corner - suit only */}
                  <div className="absolute top-5 left-5">
                    <SuitIcon
                      className={`w-10 h-10 ${suitColor}`}
                      fill="currentColor"
                    />
                  </div>

                  {/* Center content */}
                  <div className="text-center px-6">
                    <span
                      className={`
                        font-bold text-gray-800
                        ${alias.lang === 'zh' ? 'text-4xl' : 'text-3xl'}
                      `}
                    >
                      {alias.name}
                    </span>
                  </div>

                  {/* Bottom right corner - suit only */}
                  <div className="absolute bottom-5 right-5 rotate-180">
                    <SuitIcon
                      className={`w-10 h-10 ${suitColor}`}
                      fill="currentColor"
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Static fallback for screen readers */}
      <div className="sr-only">
        Also known as: {gameAliases.map((a) => a.name).join(', ')}
      </div>
    </div>
  );
}
